import { useState } from 'react';
import { useAccounts, useAccountFilterOptions, useExportAccounts, AccountFilters } from '@/hooks/useAccounts';
import { AccountsTable, AccountsFilterPanel, type AccountsTableVariant } from '@/components/accounts';
import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { convertToCSV, downloadFile, formatDateForFilename } from '@/lib/export-utils';
import { toast } from 'sonner';

interface SegmentDirectoryProps {
  /** carr_segment value this directory is scoped to (e.g. 'cro_cdmo', 'review'). */
  segment: string;
  title: string;
  subtitle: string;
  /** When false, shows the entire segment regardless of is_icp_relevant (SP / review). */
  requireIcpRelevant?: boolean;
  /** Filename stem for CSV export. */
  exportStem: string;
  /** Column set for the table (defaults to therapeutic). */
  variant?: AccountsTableVariant;
}

/**
 * Reusable account directory scoped to a single carr_segment. Mirrors the
 * Accounts page look/feel (filter panel + table + CSV export) but drops the
 * rep-scoping/URL-sync that only the core Therapeutic Developer tab needs.
 */
export default function SegmentDirectory({
  segment,
  title,
  subtitle,
  requireIcpRelevant = false,
  exportStem,
  variant = 'therapeutic',
}: SegmentDirectoryProps) {
  const [filters, setFilters] = useState<AccountFilters>({
    limit: 25,
    offset: 0,
    hideInactive: true,
    segment,
    requireIcpRelevant,
  });

  const { data: filterOptions } = useAccountFilterOptions();
  const { data: result, isLoading, error, refetch } = useAccounts(filters);
  const exportMutation = useExportAccounts();

  const accounts = result?.data || [];
  const totalCount = result?.totalCount ?? 0;
  const pageSize = filters.limit || 25;
  const pageIndex = Math.floor((filters.offset || 0) / pageSize);

  const resetFilters = () =>
    setFilters({ limit: 25, offset: 0, hideInactive: true, segment, requireIcpRelevant });

  const handleExport = async () => {
    try {
      const data = await exportMutation.mutateAsync(filters);
      const exportData = data.map((account) => ({
        'Company Name': account.company_name || '',
        'Domain': account.domain || '',
        'Category': account.primary_category || '',
        'Subcategory': account.ai_subcategory || '',
        'Region': account.region || '',
        'Country': account.headquarters_country || '',
        'State': account.headquarters_state || '',
        'Sales Rep': account.sales_rep || '',
        'Employee Range': account.employee_range || '',
      }));
      const csv = convertToCSV(exportData);
      downloadFile(csv, `${exportStem}_${formatDateForFilename()}.csv`, 'text/csv;charset=utf-8;');
      toast.success(`Exported ${data.length} accounts`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export accounts');
    }
  };

  const handlePageSizeChange = (size: number) =>
    setFilters((prev) => ({ ...prev, limit: size, offset: 0 }));
  const handlePageIndexChange = (index: number) => {
    setFilters((prev) => ({ ...prev, offset: index * (prev.limit || 25) }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (error) {
    return (
      <div className="p-8">
        <div className="space-y-1 mb-6">
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-muted-foreground">{subtitle}</p>
        </div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          <p className="font-semibold">Error loading accounts</p>
          <p className="text-sm mt-1">{error.message}</p>
          <Button variant="outline" className="mt-3" onClick={() => refetch()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-muted-foreground">{subtitle}</p>
        </div>
        <Button
          onClick={handleExport}
          disabled={exportMutation.isPending || totalCount === 0}
          variant="outline"
        >
          <Download className="mr-2 h-4 w-4" />
          {exportMutation.isPending ? 'Exporting...' : `Export CSV (${totalCount.toLocaleString()})`}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <AccountsFilterPanel
            filters={filters}
            onFiltersChange={setFilters}
            filterOptions={filterOptions}
            totalCount={totalCount}
          />
        </div>

        <div className="lg:col-span-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <p className="text-lg font-medium">No accounts found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
              <Button variant="outline" className="mt-4" onClick={resetFilters}>
                Clear All Filters
              </Button>
            </div>
          ) : (
            <AccountsTable
              data={accounts}
              totalCount={totalCount}
              pageSize={pageSize}
              pageIndex={pageIndex}
              onPageSizeChange={handlePageSizeChange}
              onPageIndexChange={handlePageIndexChange}
              variant={variant}
            />
          )}
        </div>
      </div>
    </div>
  );
}
